import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle } from 'lucide-react';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-purple-700 text-white py-16 pt-32">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-purple-200 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <FileText className="w-10 h-10 text-amber-400" />
            <h1 className="text-4xl font-bold">Terms & Conditions</h1>
          </div>
          <p className="text-purple-200 mt-2">Last updated: March 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          
          {/* Important Notice */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-amber-800 text-lg">IMPORTANT: Research Use Only</h3>
                <p className="text-amber-700 mt-2">
                  All products sold by AminoChain are intended for <strong>laboratory research purposes only</strong>. 
                  These products are <strong>NOT intended for human or animal consumption</strong>, therapeutic use, 
                  or any diagnostic procedures. By purchasing from AminoChain, you acknowledge and agree that you 
                  will use these products solely for legitimate research purposes.
                </p>
              </div>
            </div>
          </div>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing and using the AminoChain website and purchasing our products, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, you must not use our website or purchase our products.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Product Use Restrictions</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              All peptides and research compounds sold by AminoChain are strictly for:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>In-vitro laboratory research</li>
              <li>Educational purposes</li>
              <li>Scientific investigation by qualified researchers</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              <strong>Prohibited uses include but are not limited to:</strong>
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-2">
              <li>Human consumption or ingestion</li>
              <li>Animal consumption or veterinary use</li>
              <li>Therapeutic, diagnostic, or medical purposes</li>
              <li>Food additives or supplements</li>
              <li>Any use that violates local, state, or federal laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Purchaser Requirements</h2>
            <p className="text-gray-600 leading-relaxed mb-4">By making a purchase, you represent and warrant that:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>You are at least 18 years of age</li>
              <li>You have the legal authority to enter into this agreement</li>
              <li>You are purchasing for legitimate research purposes only</li>
              <li>You will comply with all applicable laws and regulations regarding the handling, storage, and use of research chemicals</li>
              <li>You have appropriate facilities and training for handling research compounds</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Product Information</h2>
            <p className="text-gray-600 leading-relaxed">
              AminoChain strives to provide accurate product descriptions, including purity levels and specifications. All products are accompanied by Certificates of Analysis (COA). While we make every effort to ensure accuracy, we do not warrant that product descriptions or other content on our website are error-free, complete, or current.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Pricing and Payment</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              All prices are listed in US dollars and are subject to change without notice. We reserve the right to correct any pricing errors. Payment must be received in full before orders are processed and shipped.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We accept various payment methods as displayed during checkout. All transactions are processed securely through our authorized payment processors.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Order Acceptance</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to refuse or cancel any order for any reason, including but not limited to product availability, errors in product or pricing information, or suspected fraudulent activity. If we cancel an order after payment has been processed, we will issue a full refund.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              All content on the AminoChain website, including text, graphics, logos, images, and software, is the property of AminoChain or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law, AminoChain shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our products or website. Our total liability shall not exceed the amount paid for the specific product giving rise to the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Indemnification</h2>
            <p className="text-gray-600 leading-relaxed">
              You agree to indemnify, defend, and hold harmless AminoChain and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorney's fees) arising from your use of our products, violation of these terms, or violation of any rights of a third party.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under these terms shall be resolved in the appropriate courts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              AminoChain reserves the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to our website. Your continued use of the website following any changes constitutes acceptance of the revised terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <p className="text-gray-600 leading-relaxed">
              For questions regarding these Terms and Conditions, please contact us:
            </p>
            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
              <p className="font-semibold text-purple-900">AminoChain</p>
              <p className="text-purple-700">Email: support@amino-chain.com</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPage;
